module.exports = {
vertEnv : `
varying vec3 vNormal;
void main() {
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position, 1.0 );
  vNormal = vec3(normal);
}
`,
fragEnv : `
uniform float darkest;
uniform float brightest;
uniform vec3 light;
varying vec3 vNormal;
void main(){
  vec3 l = normalize(light);
  float ndl = dot(vNormal,l);
  ndl = ndl*(brightest-darkest)+darkest;
  gl_FragColor = vec4(ndl,ndl,ndl,1.0);
}
`,

vertFig : `
uniform vec3 noiseseed;
uniform float noiseparam1;
uniform float noiseparam2;
uniform float noiseparam3;

varying vec2 vUv;
varying float noise;

//
// GLSL textureless classic 3D noise "cnoise",
// with an RSL-style periodic variant "pnoise".
// Author:  Stefan Gustavson (stefan.gustavson@liu.se)
// Version: 2011-10-11
//
// Many thanks to Ian McEwan of Ashima Arts for the
// ideas for permutation and gradient selection.
//
// Copyright (c) 2011 Stefan Gustavson. All rights reserved.
// Distributed under the MIT license. See LICENSE file.
// https://github.com/stegu/webgl-noise
//

vec3 mod289(vec3 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x)
{
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x)
{
  return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r)
{
  return 1.79284291400159 - 0.85373472095314 * r;
}

vec3 fade(vec3 t) {
  return t*t*t*(t*(t*6.0-15.0)+10.0);
}

// Classic Perlin noise
float cnoise(vec3 P)
{
  vec3 Pi0 = floor(P); // Integer part for indexing
  vec3 Pi1 = Pi0 + vec3(1.0); // Integer part + 1
  Pi0 = mod289(Pi0);
  Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
  return 2.2 * n_xyz;
}

// Classic Perlin noise, periodic variant
float pnoise(vec3 P, vec3 rep)
{
  vec3 Pi0 = mod(floor(P), rep); // Integer part, modulo period
  vec3 Pi1 = mod(Pi0 + vec3(1.0), rep); // Integer part + 1, mod period
  Pi0 = mod289(Pi0);
  Pi1 = mod289(Pi1);
  vec3 Pf0 = fract(P); // Fractional part for interpolation
  vec3 Pf1 = Pf0 - vec3(1.0); // Fractional part - 1.0
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;

  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);

  vec4 gx0 = ixy0 * (1.0 / 7.0);
  vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);

  vec4 gx1 = ixy1 * (1.0 / 7.0);
  vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);

  vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
  vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
  vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
  vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
  vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
  vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
  vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
  vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x;
  g010 *= norm0.y;
  g100 *= norm0.z;
  g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x;
  g011 *= norm1.y;
  g101 *= norm1.z;
  g111 *= norm1.w;

  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);

  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
  return 2.2 * n_xyz;
}
void main() {
    noise = pnoise((position+noiseseed) * noiseparam1, vec3(noiseparam3,noiseparam3,noiseparam3));
    gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position+noiseparam2*normal*noise, 1.0 );
    vUv = uv;
}
`,

fragFig : `
uniform vec3 color;
uniform float alpha;
varying vec2 vUv;
varying float noise;
void main() {
    gl_FragColor = vec4( color*(noise*0.3+0.7)+0.01, alpha );
}
`,
  
vertShadow : `
varying vec2 vUv;
void main() {
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position, 1.0 );
  vUv = uv;
}
`,
fragShadow : `
varying vec2 vUv;
uniform vec3 color;
void main(){
  float l = length(vUv-vec2(0.5,0.5));
  float sig =0.15;
  float ls = l/sig;
  float g = 0.398*pow(2.718,-0.5*ls*ls)/sig*0.1;
  gl_FragColor = vec4(color,g);
}
`,

vertFloor : `
varying vec2 vUv;
void main() {
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position, 1.0 );
  vUv = uv;
}
`,

fragFloor:`
varying vec2 vUv;

uniform vec4 u00;
uniform vec4 u01;
uniform vec4 u02;
uniform vec4 u03;
uniform vec4 u04;
uniform vec4 u05;
uniform vec4 u06;
uniform vec4 u07;
uniform vec4 u08;
uniform vec4 u09;
uniform vec4 u10;
uniform vec4 u11;
uniform vec4 u12;
uniform vec4 u13;
uniform vec4 u14;
uniform vec4 u15;
uniform vec4 u16;
uniform vec4 u17;
uniform vec4 u18;
uniform vec4 u19;
uniform vec4 u20;
uniform vec4 u21;
uniform vec4 u22;
uniform vec4 u23;
uniform vec4 u24;
uniform vec4 u25;
uniform vec4 u26;
uniform vec4 u27;
uniform vec4 u28;
uniform vec4 u29;
uniform vec4 u30;
uniform vec4 u31;
uniform vec4 u32;
uniform vec4 u33;
uniform vec4 u34;
uniform vec4 u35;
uniform vec4 u36;
uniform vec4 u37;
uniform vec4 u38;
uniform vec4 u39;
uniform vec4 u40;
uniform vec4 u41;
uniform vec4 u42;
uniform vec4 u43;
uniform vec4 u44;
uniform vec4 u45;
uniform vec4 u46;
uniform vec4 u47;
uniform vec4 u48;
uniform vec4 u49;
uniform vec4 u50;
uniform vec4 u51;
uniform vec4 u52;
uniform vec4 u53;
uniform vec4 u54;
uniform vec4 u55;
uniform vec4 u56;
uniform vec4 u57;
uniform vec4 u58;
uniform vec4 u59;
uniform vec4 u60;
uniform vec4 u61;
uniform vec4 u62;
uniform vec4 u63;
uniform vec4 u64;
uniform vec4 u65;
uniform vec4 u66;
uniform vec4 u67;
uniform vec4 u68;
uniform vec4 u69;
uniform vec4 u70;
uniform vec4 u71;
uniform vec4 u72;
uniform vec4 u73;
uniform vec4 u74;
uniform vec4 u75;
uniform vec4 u76;
uniform vec4 u77;
uniform vec4 u78;
uniform vec4 u79;
uniform vec4 u80;
uniform vec4 u81;
uniform vec4 u82;
uniform vec4 u83;
uniform vec4 u84;
uniform vec4 u85;
uniform vec4 u86;
uniform vec4 u87;
uniform vec4 u88;
uniform vec4 u89;
uniform vec4 u90;
uniform vec4 u91;
uniform vec4 u92;
uniform vec4 u93;
uniform vec4 u94;
uniform vec4 u95;
uniform vec4 u96;
uniform vec4 u97;
uniform vec4 u98;
uniform vec4 u99;


uniform vec3 c00;
uniform vec3 c01;
uniform vec3 c02;
uniform vec3 c03;
uniform vec3 c04;
uniform vec3 c05;
uniform vec3 c06;
uniform vec3 c07;
uniform vec3 c08;
uniform vec3 c09;
uniform vec3 c10;
uniform vec3 c11;
uniform vec3 c12;
uniform vec3 c13;
uniform vec3 c14;
uniform vec3 c15;
uniform vec3 c16;
uniform vec3 c17;
uniform vec3 c18;
uniform vec3 c19;
uniform vec3 c20;
uniform vec3 c21;
uniform vec3 c22;
uniform vec3 c23;
uniform vec3 c24;
uniform vec3 c25;
uniform vec3 c26;
uniform vec3 c27;
uniform vec3 c28;
uniform vec3 c29;
uniform vec3 c30;
uniform vec3 c31;
uniform vec3 c32;
uniform vec3 c33;
uniform vec3 c34;
uniform vec3 c35;
uniform vec3 c36;
uniform vec3 c37;
uniform vec3 c38;
uniform vec3 c39;
uniform vec3 c40;
uniform vec3 c41;
uniform vec3 c42;
uniform vec3 c43;
uniform vec3 c44;
uniform vec3 c45;
uniform vec3 c46;
uniform vec3 c47;
uniform vec3 c48;
uniform vec3 c49;
uniform vec3 c50;
uniform vec3 c51;
uniform vec3 c52;
uniform vec3 c53;
uniform vec3 c54;
uniform vec3 c55;
uniform vec3 c56;
uniform vec3 c57;
uniform vec3 c58;
uniform vec3 c59;
uniform vec3 c60;
uniform vec3 c61;
uniform vec3 c62;
uniform vec3 c63;
uniform vec3 c64;
uniform vec3 c65;
uniform vec3 c66;
uniform vec3 c67;
uniform vec3 c68;
uniform vec3 c69;
uniform vec3 c70;
uniform vec3 c71;
uniform vec3 c72;
uniform vec3 c73;
uniform vec3 c74;
uniform vec3 c75;
uniform vec3 c76;
uniform vec3 c77;
uniform vec3 c78;
uniform vec3 c79;
uniform vec3 c80;
uniform vec3 c81;
uniform vec3 c82;
uniform vec3 c83;
uniform vec3 c84;
uniform vec3 c85;
uniform vec3 c86;
uniform vec3 c87;
uniform vec3 c88;
uniform vec3 c89;
uniform vec3 c90;
uniform vec3 c91;
uniform vec3 c92;
uniform vec3 c93;
uniform vec3 c94;
uniform vec3 c95;
uniform vec3 c96;
uniform vec3 c97;
uniform vec3 c98;
uniform vec3 c99;

void main(){
  vec4 l = vec4(0,0,0,0);
  l += max(0.0,u00.w-length(vUv-u00.xy))*u00.z*vec4(c00,1.0);
  l += max(0.0,u01.w-length(vUv-u01.xy))*u01.z*vec4(c01,1.0);
  l += max(0.0,u02.w-length(vUv-u02.xy))*u02.z*vec4(c02,1.0);
  l += max(0.0,u03.w-length(vUv-u03.xy))*u03.z*vec4(c03,1.0);
  l += max(0.0,u04.w-length(vUv-u04.xy))*u04.z*vec4(c04,1.0);
  l += max(0.0,u05.w-length(vUv-u05.xy))*u05.z*vec4(c05,1.0);
  l += max(0.0,u06.w-length(vUv-u06.xy))*u06.z*vec4(c06,1.0);
  l += max(0.0,u07.w-length(vUv-u07.xy))*u07.z*vec4(c07,1.0);
  l += max(0.0,u08.w-length(vUv-u08.xy))*u08.z*vec4(c08,1.0);
  l += max(0.0,u09.w-length(vUv-u09.xy))*u09.z*vec4(c09,1.0);
  l += max(0.0,u10.w-length(vUv-u10.xy))*u10.z*vec4(c10,1.0);
  l += max(0.0,u11.w-length(vUv-u11.xy))*u11.z*vec4(c11,1.0);
  l += max(0.0,u12.w-length(vUv-u12.xy))*u12.z*vec4(c12,1.0);
  l += max(0.0,u13.w-length(vUv-u13.xy))*u13.z*vec4(c13,1.0);
  l += max(0.0,u14.w-length(vUv-u14.xy))*u14.z*vec4(c14,1.0);
  l += max(0.0,u15.w-length(vUv-u15.xy))*u15.z*vec4(c15,1.0);
  l += max(0.0,u16.w-length(vUv-u16.xy))*u16.z*vec4(c16,1.0);
  l += max(0.0,u17.w-length(vUv-u17.xy))*u17.z*vec4(c17,1.0);
  l += max(0.0,u18.w-length(vUv-u18.xy))*u18.z*vec4(c18,1.0);
  l += max(0.0,u19.w-length(vUv-u19.xy))*u19.z*vec4(c19,1.0);
  l += max(0.0,u20.w-length(vUv-u20.xy))*u20.z*vec4(c20,1.0);
  l += max(0.0,u21.w-length(vUv-u21.xy))*u21.z*vec4(c21,1.0);
  l += max(0.0,u22.w-length(vUv-u22.xy))*u22.z*vec4(c22,1.0);
  l += max(0.0,u23.w-length(vUv-u23.xy))*u23.z*vec4(c23,1.0);
  l += max(0.0,u24.w-length(vUv-u24.xy))*u24.z*vec4(c24,1.0);
  l += max(0.0,u25.w-length(vUv-u25.xy))*u25.z*vec4(c25,1.0);
  l += max(0.0,u26.w-length(vUv-u26.xy))*u26.z*vec4(c26,1.0);
  l += max(0.0,u27.w-length(vUv-u27.xy))*u27.z*vec4(c27,1.0);
  l += max(0.0,u28.w-length(vUv-u28.xy))*u28.z*vec4(c28,1.0);
  l += max(0.0,u29.w-length(vUv-u29.xy))*u29.z*vec4(c29,1.0);
  l += max(0.0,u30.w-length(vUv-u30.xy))*u30.z*vec4(c30,1.0);
  l += max(0.0,u31.w-length(vUv-u31.xy))*u31.z*vec4(c31,1.0);
  l += max(0.0,u32.w-length(vUv-u32.xy))*u32.z*vec4(c32,1.0);
  l += max(0.0,u33.w-length(vUv-u33.xy))*u33.z*vec4(c33,1.0);
  l += max(0.0,u34.w-length(vUv-u34.xy))*u34.z*vec4(c34,1.0);
  l += max(0.0,u35.w-length(vUv-u35.xy))*u35.z*vec4(c35,1.0);
  l += max(0.0,u36.w-length(vUv-u36.xy))*u36.z*vec4(c36,1.0);
  l += max(0.0,u37.w-length(vUv-u37.xy))*u37.z*vec4(c37,1.0);
  l += max(0.0,u38.w-length(vUv-u38.xy))*u38.z*vec4(c38,1.0);
  l += max(0.0,u39.w-length(vUv-u39.xy))*u39.z*vec4(c39,1.0);
  l += max(0.0,u40.w-length(vUv-u40.xy))*u40.z*vec4(c40,1.0);
  l += max(0.0,u41.w-length(vUv-u41.xy))*u41.z*vec4(c41,1.0);
  l += max(0.0,u42.w-length(vUv-u42.xy))*u42.z*vec4(c42,1.0);
  l += max(0.0,u43.w-length(vUv-u43.xy))*u43.z*vec4(c43,1.0);
  l += max(0.0,u44.w-length(vUv-u44.xy))*u44.z*vec4(c44,1.0);
  l += max(0.0,u45.w-length(vUv-u45.xy))*u45.z*vec4(c45,1.0);
  l += max(0.0,u46.w-length(vUv-u46.xy))*u46.z*vec4(c46,1.0);
  l += max(0.0,u47.w-length(vUv-u47.xy))*u47.z*vec4(c47,1.0);
  l += max(0.0,u48.w-length(vUv-u48.xy))*u48.z*vec4(c48,1.0);
  l += max(0.0,u49.w-length(vUv-u49.xy))*u49.z*vec4(c49,1.0);
  l += max(0.0,u50.w-length(vUv-u50.xy))*u50.z*vec4(c50,1.0);
  l += max(0.0,u51.w-length(vUv-u51.xy))*u51.z*vec4(c51,1.0);
  l += max(0.0,u52.w-length(vUv-u52.xy))*u52.z*vec4(c52,1.0);
  l += max(0.0,u53.w-length(vUv-u53.xy))*u53.z*vec4(c53,1.0);
  l += max(0.0,u54.w-length(vUv-u54.xy))*u54.z*vec4(c54,1.0);
  l += max(0.0,u55.w-length(vUv-u55.xy))*u55.z*vec4(c55,1.0);
  l += max(0.0,u56.w-length(vUv-u56.xy))*u56.z*vec4(c56,1.0);
  l += max(0.0,u57.w-length(vUv-u57.xy))*u57.z*vec4(c57,1.0);
  l += max(0.0,u58.w-length(vUv-u58.xy))*u58.z*vec4(c58,1.0);
  l += max(0.0,u59.w-length(vUv-u59.xy))*u59.z*vec4(c59,1.0);
  l += max(0.0,u60.w-length(vUv-u60.xy))*u60.z*vec4(c60,1.0);
  l += max(0.0,u61.w-length(vUv-u61.xy))*u61.z*vec4(c61,1.0);
  l += max(0.0,u62.w-length(vUv-u62.xy))*u62.z*vec4(c62,1.0);
  l += max(0.0,u63.w-length(vUv-u63.xy))*u63.z*vec4(c63,1.0);
  l += max(0.0,u64.w-length(vUv-u64.xy))*u64.z*vec4(c64,1.0);
  l += max(0.0,u65.w-length(vUv-u65.xy))*u65.z*vec4(c65,1.0);
  l += max(0.0,u66.w-length(vUv-u66.xy))*u66.z*vec4(c66,1.0);
  l += max(0.0,u67.w-length(vUv-u67.xy))*u67.z*vec4(c67,1.0);
  l += max(0.0,u68.w-length(vUv-u68.xy))*u68.z*vec4(c68,1.0);
  l += max(0.0,u69.w-length(vUv-u69.xy))*u69.z*vec4(c69,1.0);
  l += max(0.0,u70.w-length(vUv-u70.xy))*u70.z*vec4(c70,1.0);
  l += max(0.0,u71.w-length(vUv-u71.xy))*u71.z*vec4(c71,1.0);
  l += max(0.0,u72.w-length(vUv-u72.xy))*u72.z*vec4(c72,1.0);
  l += max(0.0,u73.w-length(vUv-u73.xy))*u73.z*vec4(c73,1.0);
  l += max(0.0,u74.w-length(vUv-u74.xy))*u74.z*vec4(c74,1.0);
  l += max(0.0,u75.w-length(vUv-u75.xy))*u75.z*vec4(c75,1.0);
  l += max(0.0,u76.w-length(vUv-u76.xy))*u76.z*vec4(c76,1.0);
  l += max(0.0,u77.w-length(vUv-u77.xy))*u77.z*vec4(c77,1.0);
  l += max(0.0,u78.w-length(vUv-u78.xy))*u78.z*vec4(c78,1.0);
  l += max(0.0,u79.w-length(vUv-u79.xy))*u79.z*vec4(c79,1.0);
  l += max(0.0,u80.w-length(vUv-u80.xy))*u80.z*vec4(c80,1.0);
  l += max(0.0,u81.w-length(vUv-u81.xy))*u81.z*vec4(c81,1.0);
  l += max(0.0,u82.w-length(vUv-u82.xy))*u82.z*vec4(c82,1.0);
  l += max(0.0,u83.w-length(vUv-u83.xy))*u83.z*vec4(c83,1.0);
  l += max(0.0,u84.w-length(vUv-u84.xy))*u84.z*vec4(c84,1.0);
  l += max(0.0,u85.w-length(vUv-u85.xy))*u85.z*vec4(c85,1.0);
  l += max(0.0,u86.w-length(vUv-u86.xy))*u86.z*vec4(c86,1.0);
  l += max(0.0,u87.w-length(vUv-u87.xy))*u87.z*vec4(c87,1.0);
  l += max(0.0,u88.w-length(vUv-u88.xy))*u88.z*vec4(c88,1.0);
  l += max(0.0,u89.w-length(vUv-u89.xy))*u89.z*vec4(c89,1.0);
  l += max(0.0,u90.w-length(vUv-u90.xy))*u90.z*vec4(c90,1.0);
  l += max(0.0,u91.w-length(vUv-u91.xy))*u91.z*vec4(c91,1.0);
  l += max(0.0,u92.w-length(vUv-u92.xy))*u92.z*vec4(c92,1.0);
  l += max(0.0,u93.w-length(vUv-u93.xy))*u93.z*vec4(c93,1.0);
  l += max(0.0,u94.w-length(vUv-u94.xy))*u94.z*vec4(c94,1.0);
  l += max(0.0,u95.w-length(vUv-u95.xy))*u95.z*vec4(c95,1.0);
  l += max(0.0,u96.w-length(vUv-u96.xy))*u96.z*vec4(c96,1.0);
  l += max(0.0,u97.w-length(vUv-u97.xy))*u97.z*vec4(c97,1.0);
  l += max(0.0,u98.w-length(vUv-u98.xy))*u98.z*vec4(c98,1.0);
  l += max(0.0,u99.w-length(vUv-u99.xy))*u99.z*vec4(c99,1.0);

  vec4 g = l;
  g.x = min(g.x,1.0);
  g.y = min(g.y,1.0);
  g.z = min(g.z,1.0);
  g.w = min(g.w,1.0);
  gl_FragColor = g;
}
`

}